import type { IconButtonProps } from '@mui/material/IconButton';

import { varAlpha } from 'minimal-shared/utils';
import { useBoolean } from 'minimal-shared/hooks';

import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Avatar from '@mui/material/Avatar';
import Drawer from '@mui/material/Drawer';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';

import { paths } from 'src/routes/paths';
import { usePathname } from 'src/routes/hooks';
import { RouterLink } from 'src/routes/components';

import { useTranslate } from 'src/locales';

import { Label } from 'src/components/label';
import { Iconify } from 'src/components/iconify';
import { Scrollbar } from 'src/components/scrollbar';
import { AnimateBorder } from 'src/components/animate';

import { useAuthContext } from 'src/auth/hooks';
import { UserProfileCreationView } from 'src/auth/view/user-profile-creation-view';

import { AccountButton } from './account-button';
import { SignOutButton } from './sign-out-button';
import { LanguageToggle } from './language-toggle';
import { DealerCreditInfo } from './dealer-credit-info';

// ----------------------------------------------------------------------

export type AccountDrawerProps = IconButtonProps & {
  data?: {
    label: string;
    href: string;
    icon?: React.ReactNode;
    info?: React.ReactNode;
  }[];
};

export function AccountDrawer({ data = [], sx, ...other }: AccountDrawerProps) {
  const pathname = usePathname();

  const { t } = useTranslate('layout');

  const { user } = useAuthContext();

  const { value: open, onFalse: onClose, onTrue: onOpen } = useBoolean();
  const {
    value: openProfileEdit,
    onFalse: onCloseProfileEdit,
    onTrue: onOpenProfileEdit,
  } = useBoolean();

  const renderAvatar = () => (
    <AnimateBorder
      sx={{ mb: 2, p: '6px', width: 96, height: 96, borderRadius: '50%' }}
      slotProps={{
        primaryBorder: { size: 120, sx: { color: 'primary.main' } },
      }}
    >
      <Avatar src={user?.photoURL} alt={user?.displayName} sx={{ width: 1, height: 1 }}>
        {user?.displayName?.charAt(0).toUpperCase()}
      </Avatar>
    </AnimateBorder>
  );

  const renderList = () => (
    <MenuList
      disablePadding
      sx={[
        (theme) => ({
          py: 3,
          px: 2.5,
          borderTop: `dashed 1px ${theme.vars.palette.divider}`,
          borderBottom: `dashed 1px ${theme.vars.palette.divider}`,
          '& li': { p: 0 },
        }),
      ]}
    >
      <MenuItem>
        <Box
          onClick={() => {
            onOpenProfileEdit();
            onClose();
          }}
          sx={{
            p: 1,
            width: 1,
            display: 'flex',
            typography: 'body2',
            alignItems: 'center',
            color: 'text.secondary',
            cursor: 'pointer',
            '& svg': { width: 24, height: 24 },
            '&:hover': { color: 'text.primary' },
          }}
        >
          <Iconify icon="solar:user-rounded-bold" />
          <Box component="span" sx={{ ml: 2 }}>
            {t('accountDrawer.editProfile')}
          </Box>
        </Box>
      </MenuItem>

      {data.map((option) => {
        const rootLabel = pathname.includes('/dashboard')
          ? t('accountDrawer.home')
          : t('accountDrawer.dashboard');
        const rootHref = pathname.includes('/dashboard') ? '/' : paths.dashboard.root;

        return (
          <MenuItem key={option.label}>
            <Link
              component={RouterLink}
              href={option.label === 'Home' ? rootHref : option.href}
              color="inherit"
              underline="none"
              onClick={onClose}
              sx={{
                p: 1,
                width: 1,
                display: 'flex',
                typography: 'body2',
                alignItems: 'center',
                color: 'text.secondary',
                '& svg': { width: 24, height: 24 },
                '&:hover': { color: 'text.primary' },
              }}
            >
              {option.icon}

              <Box component="span" sx={{ ml: 2 }}>
                {option.label === 'Home' ? rootLabel : option.label}
              </Box>

              {option.info && (
                <Label color="error" sx={{ ml: 1 }}>
                  {option.info}
                </Label>
              )}
            </Link>
          </MenuItem>
        );
      })}
    </MenuList>
  );

  return (
    <>
      <AccountButton
        onClick={onOpen}
        photoURL={user?.photoURL}
        displayName={user?.displayName}
        sx={sx}
        {...other}
      />

      <Drawer
        open={open}
        onClose={onClose}
        anchor="right"
        slotProps={{
          backdrop: { invisible: true },
          paper: { sx: { width: 320 } },
        }}
      >
        <IconButton
          onClick={onClose}
          sx={{
            top: 12,
            left: 12,
            zIndex: 9,
            position: 'absolute',
          }}
        >
          <Iconify icon="mingcute:close-line" />
        </IconButton>

        <Scrollbar>
          <Box
            sx={{
              pt: 8,
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'column',
            }}
          >
            {renderAvatar()}

            <Typography variant="subtitle1" noWrap sx={{ mt: 2 }}>
              {user?.displayName}
            </Typography>

            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }} noWrap>
              {user?.email}
            </Typography>

            {/* Dealer Information */}
            <Box
              sx={{
                mt: 2,
                gap: 1,
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {user?.status && (
                <Label
                  color={
                    user.status === 'ACTIVE'
                      ? 'success'
                      : user.status === 'KYC_PENDING'
                        ? 'warning'
                        : 'error'
                  }
                  variant="soft"
                >
                  {user.status}
                </Label>
              )}

              {user?.profileData?.role && (
                <Label color="info" variant="soft">
                  {user.profileData.role}
                </Label>
              )}
            </Box>

            {/* Office Location */}
            {user?.office && (
              <Box
                sx={{
                  mt: 1.5,
                  px: 2,
                  py: 1,
                  mx: 2,
                  borderRadius: 1,
                  bgcolor: (theme) => varAlpha(theme.vars.palette.grey['500Channel'], 0.08),
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <Iconify icon="solar:map-point-bold" width={16} sx={{ mr: 0.5 }} />
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {t('accountDrawer.officeLocation')}
                  </Typography>
                </Box>
                <Typography variant="caption" sx={{ color: 'text.primary' }}>
                  {user.office.area && `${user.office.area}, `}
                  {user.office.city}
                  {user.office.state && `, ${user.office.state}`}
                </Typography>
              </Box>
            )}
          </Box>

          {renderList()}

          <Box sx={{ px: 2.5, py: 3 }}>
            <DealerCreditInfo />
          </Box>
        </Scrollbar>

        <Box
          sx={{ px: 2.5, py: 2, borderTop: (theme) => `dashed 1px ${theme.vars.palette.divider}` }}
        >
          <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1.5, display: 'block' }}>
            {t('accountDrawer.language')}
          </Typography>
          <LanguageToggle
            fullWidth
            sx={{
              '& .MuiToggleButton-root': {
                py: 0.75,
              },
            }}
          />
        </Box>

        <Box sx={{ p: 2.5 }}>
          <SignOutButton onClose={onClose} />
        </Box>
      </Drawer>

      <UserProfileCreationView open={openProfileEdit} onClose={onCloseProfileEdit} mode="update" />
    </>
  );
}
